import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Chart from 'react-apexcharts';

function formatHours(value) {
  const hours = Number(value);
  if (!Number.isFinite(hours)) return '—';
  if (hours < 48) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours - days * 24);
  return remainingHours ? `${days}d ${remainingHours}h` : `${days}d`;
}

function shortDate(value) {
  const parts = String(value || '').substring(0, 10).split('-');
  if (parts.length !== 3) return value || '';
  return `${parts[2]}/${parts[1]}`;
}

function Stat({ label, value }) {
  return (
    <Box minWidth={0}>
      <Typography sx={{ color: '#64748b', fontSize: '0.68rem' }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#0f172a', fontWeight: 800, fontSize: '0.9rem' }}>
        {value}
      </Typography>
    </Box>
  );
}

function ClientsActivationChart({ response, status }) {
  const [chartView, setChartView] = React.useState('curve');
  const loading = status === 'loading';
  const curve = response?.activation_curve || { eligible_30d: 0, rows: [] };
  const trend = response?.trend || [];
  const selected = response?.summary?.selected_window || {};
  const windowDays = Number(response?.filters?.windowDays || selected.days || 7);
  const hasResponse = Boolean(response);
  const curveHasPopulation = Number(curve.eligible_30d || 0) > 0;
  const partialTrendBuckets = trend.filter((row) => row.complete === false).length;

  const curveOptions = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      selection: { enabled: false },
      fontFamily: 'inherit',
    },
    colors: ['#2563eb'],
    dataLabels: { enabled: false },
    stroke: { width: 3, curve: 'smooth' },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 0.25, opacityFrom: 0.3, opacityTo: 0.04 },
    },
    grid: { borderColor: '#e2e8f0', strokeDashArray: 4 },
    xaxis: {
      categories: curve.rows.map((row) => `${row.elapsedDays}d`),
      tickAmount: 7,
      labels: { style: { colors: '#64748b', fontSize: '11px' } },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (value) => `${Math.round(value)}%`,
        style: { colors: '#64748b' },
      },
    },
    tooltip: {
      y: {
        formatter: (value, context) => {
          const row = curve.rows[context.dataPointIndex] || {};
          return `${Number(value || 0).toFixed(1)}% · ${Number(row.converted || 0).toLocaleString('ro-RO')} of ${Number(curve.eligible_30d || 0).toLocaleString('ro-RO')} clients`;
        },
      },
    },
  };
  const curveSeries = [{
    name: 'Activation rate',
    data: curve.rows.map((row) => row.rate),
  }];

  const trendOptions = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      selection: { enabled: false },
      fontFamily: 'inherit',
    },
    colors: ['#64748b', '#1d4ed8'],
    dataLabels: { enabled: false },
    stroke: { width: [0, 4], curve: 'smooth', dashArray: [0, 0] },
    markers: {
      size: [0, 4],
      strokeWidth: 2,
      strokeColors: '#ffffff',
      hover: { sizeOffset: 2 },
    },
    fill: { opacity: [0.82, 1] },
    plotOptions: { bar: { borderRadius: 4, columnWidth: '58%' } },
    grid: { borderColor: '#e2e8f0', strokeDashArray: 4 },
    xaxis: {
      categories: trend.map((row) => (
        row.dateFrom === row.dateTo
          ? shortDate(row.dateFrom)
          : `${shortDate(row.dateFrom)}–${shortDate(row.dateTo)}`
      )),
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        style: { colors: '#64748b', fontSize: '10px' },
      },
    },
    yaxis: [
      {
        min: 0,
        labels: {
          formatter: (value) => Math.round(value).toLocaleString('ro-RO'),
          style: { colors: '#64748b' },
        },
      },
      {
        opposite: true,
        min: 0,
        max: 100,
        labels: {
          formatter: (value) => `${Math.round(value)}%`,
          style: { colors: '#64748b' },
        },
      },
    ],
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      fontWeight: 600,
    },
    tooltip: { shared: true, intersect: false },
  };
  const trendSeries = [
    {
      name: 'Registrations',
      type: 'column',
      data: trend.map((row) => Number(row.registrations || 0)),
    },
    {
      name: `${windowDays}d activation`,
      type: 'line',
      data: trend.map((row) => row.rate),
    },
  ];

  return (
    <Card
      sx={{
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
        >
          <Box>
            <Typography sx={{ color: '#111827', fontWeight: 800, fontSize: '1.02rem' }}>
              {chartView === 'curve' ? 'Activation curve' : 'Cohort trend'}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.76rem', mt: 0.15 }}>
              {chartView === 'curve'
                ? `${Number(curve.eligible_30d || 0).toLocaleString('ro-RO')} clients with a full 30-day window`
                : `Registrations and ${windowDays}-day activation${partialTrendBuckets ? ` · ${partialTrendBuckets} recent cohort${partialTrendBuckets === 1 ? '' : 's'} partial` : ''}`}
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={chartView}
            onChange={(event, value) => value && setChartView(value)}
            aria-label="Activation chart view"
            sx={{
              alignSelf: { xs: 'stretch', sm: 'center' },
              '& .MuiToggleButton-root': {
                flex: { xs: 1, sm: 'initial' },
                px: 1.5,
                py: 0.4,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.75rem',
              },
            }}
          >
            <ToggleButton value="curve">Curve</ToggleButton>
            <ToggleButton value="trend">Trend</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {loading && !hasResponse ? (
          <Skeleton variant="rounded" height={320} sx={{ mt: 1.5 }} />
        ) : chartView === 'curve' && !curveHasPopulation ? (
          <Box
            sx={{
              minHeight: 290,
              mt: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: '#f8fafc',
              borderRadius: '12px',
            }}
          >
            <Typography align="center" sx={{ color: '#64748b', fontSize: '0.82rem', px: 2 }}>
              No clients have a full 30-day observation window yet.
            </Typography>
          </Box>
        ) : (
          <Box
            role="img"
            aria-label={chartView === 'curve'
              ? `Thirty-day activation curve for ${curve.eligible_30d || 0} eligible clients`
              : `${windowDays}-day activation trend across ${trend.length} cohorts`}
            sx={{ mt: 1.25, minHeight: 310, touchAction: 'pan-y' }}
          >
            <Chart
              key={chartView}
              type={chartView === 'curve' ? 'area' : 'line'}
              height={320}
              options={chartView === 'curve' ? curveOptions : trendOptions}
              series={chartView === 'curve' ? curveSeries : trendSeries}
            />
          </Box>
        )}

        <Box
          sx={{
            mt: 1,
            pt: 1.25,
            borderTop: '1px solid #e2e8f0',
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1,
          }}
        >
          <Stat
            label={`${windowDays}d activation`}
            value={selected.rate === null || typeof selected.rate === 'undefined'
              ? '—'
              : `${Number(selected.rate).toFixed(1)}%`}
          />
          <Stat label="Median first purchase" value={formatHours(selected.median_hours)} />
          <Stat label="Unconverted" value={Number(selected.unconverted || 0).toLocaleString('ro-RO')} />
          <Stat label="Still pending" value={Number(selected.pending_maturity || 0).toLocaleString('ro-RO')} />
        </Box>
      </CardContent>
    </Card>
  );
}

export default ClientsActivationChart;
